import React from 'react';
import { useSelector } from 'store';
import type { ViewCountCell } from 'api/unread-counts';
import { useChatSidebarSystemOrder } from 'hooks/useChatSidebarSystemOrder';
import {
  SCOPES_WITHOUT_INLINE_UNASSIGNED,
  UNIFIED_KIND_TO_WORKFLOW_STATUS,
  type ChatSidebarScopeTab,
  type UnifiedListKind,
  type UnifiedStatusKind,
} from 'utils/chat-sidebar-unified-statuses';
import type { DefaultChatSidebarViewId } from 'utils/default-chat-sidebar-views';

const EMPTY: ViewCountCell = { total: 0, unread: 0 };

const DEFAULT_VIEW_ID_TO_KIND: Record<DefaultChatSidebarViewId, UnifiedStatusKind> = {
  'unassigned-chats': 'unassigned',
  'newly-assigned': 'firstReply',
  'awaiting-me': 'awaitingReply',
  'waiting-customer': 'awaitingCustomer',
  'my-chats-snoozed': 'reminder',
  'chats-completed': 'completed',
};

function sumScope(scopeCounts?: Record<string, ViewCountCell>): ViewCountCell {
  if (!scopeCounts) return EMPTY;
  let total = 0;
  let unread = 0;
  for (const key of Object.keys(scopeCounts)) {
    total += scopeCounts[key]?.total || 0;
    unread += scopeCounts[key]?.unread || 0;
  }
  return { total, unread };
}

function getSystemCount(
  scopeCounts: Record<string, ViewCountCell> | undefined,
  kind: UnifiedListKind,
): ViewCountCell {
  if (!scopeCounts) return EMPTY;
  if (kind === 'allConversations') return sumScope(scopeCounts);
  const statusId = UNIFIED_KIND_TO_WORKFLOW_STATUS[kind as UnifiedStatusKind];
  return scopeCounts[String(statusId)] ?? EMPTY;
}

export type ChatFolderUnreadTarget =
  | { type: 'unassigned' }
  | { type: 'builtin'; scope: ChatSidebarScopeTab };

/**
 * סכום unread של כל התצוגות בתיקייה (מערכת + מותאמות), בהתאם לנראות שורות ב-UnifiedChatStatusList.
 */
export function useChatFolderUnreadCount(
  target: ChatFolderUnreadTarget,
  customViewsInFolder: ReadonlyArray<{ id: string | number }>,
  statusRows: Record<string, boolean>,
): number {
  const { orderedIds } = useChatSidebarSystemOrder();
  const systemCounts = useSelector((s) => s.viewCounts.system);
  const customCounts = useSelector((s) => s.viewCounts.custom);
  const unassignedCell = useSelector((s) => s.viewCounts.unassigned);

  return React.useMemo(() => {
    let unread = 0;

    if (target.type === 'unassigned') {
      if (statusRows.unassigned !== false) {
        //* אותו תא ייעודי כמו useViewCounts (סטטוס 1 וגם בלי manager — חקירת 4.11)
        unread += (unassignedCell ?? getSystemCount(systemCounts?.all, 'unassigned')).unread;
      }
    } else {
      const scope = target.scope;
      const scopeCounts = systemCounts?.[scope];
      const allConvVisible = statusRows.allConversations !== false;

      if (allConvVisible) {
        unread += getSystemCount(scopeCounts, 'allConversations').unread;
      } else {
        const kinds = orderedIds.map((id) => DEFAULT_VIEW_ID_TO_KIND[id]);
        for (const kind of kinds) {
          if (kind === 'unassigned' && SCOPES_WITHOUT_INLINE_UNASSIGNED.includes(scope)) continue;
          if (statusRows[kind] === false) continue;
          unread +=
            kind === 'unassigned' && unassignedCell
              ? unassignedCell.unread
              : getSystemCount(scopeCounts, kind).unread;
        }
      }
    }

    for (const view of customViewsInFolder) {
      unread += customCounts[String(view.id)]?.unread ?? 0;
    }

    return unread;
  }, [target, customViewsInFolder, statusRows, orderedIds, systemCounts, customCounts, unassignedCell]);
}
